import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChevronLeft, Shield, AlertTriangle, CheckCircle2, 
  FileText, Phone, Camera, Car, ExternalLink, Info
} from 'lucide-react';

const HostInsuranceGuidance = () => {
  const navigate = useNavigate();

  const checklistItems = [
    {
      title: "Confirm peer-to-peer rental is allowed",
      description: "Contact your insurer and explicitly ask if your policy covers renting your vehicle through a peer-to-peer platform like HireHub."
    },
    {
      title: "Disclose hire/rental use",
      description: "Tell your insurer that you intend to rent your vehicle to third parties. Failure to disclose may void your coverage."
    },
    {
      title: "Check policy exclusions",
      description: "Review exclusions carefully. Some policies exclude commercial use, hire/reward, or specific driver types."
    },
    {
      title: "Understand your excess",
      description: "Know what excess (deductible) applies in the event of a claim. Consider if this changes when the vehicle is rented."
    },
    {
      title: "Verify driver coverage",
      description: "Confirm what driver requirements your policy has (age, licence type, experience). Ensure renters meet these requirements."
    },
    {
      title: "Check roadside assistance",
      description: "Understand if roadside assistance applies when the vehicle is being used by a renter."
    },
    {
      title: "Understand the claims process",
      description: "Know how to lodge a claim and what evidence is required. Keep your insurer's contact details handy."
    },
    {
      title: "Keep registration current",
      description: "Ensure your vehicle registration is valid and up to date at all times."
    },
    {
      title: "Document vehicle condition",
      description: "Take comprehensive photos before and after each rental. This protects both you and the renter."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#004B8D] rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-[#004B8D] hidden sm:inline">HireHub</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="host-insurance-guidance">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <Shield className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="page-title">
            Host Insurance Guidance
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Practical guidance for vehicle hosts listing on HireHub Australia. 
            This is not legal or insurance advice — always consult your insurer directly.
          </p>
        </div>

        {/* Important Notice Card */}
        <Card className="border-amber-200 bg-amber-50 mb-8" data-testid="important-notice">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-amber-800 mb-2">HireHub is NOT an insurer</h2>
                <p className="text-amber-700 text-sm mb-3">
                  HireHub Australia operates as a peer-to-peer marketplace only. We do not provide, 
                  arrange, or guarantee any insurance coverage for your vehicle or bookings. 
                  You are solely responsible for ensuring adequate coverage.
                </p>
                <Link 
                  to="/legal/insurance" 
                  className="inline-flex items-center text-amber-800 hover:text-amber-900 text-sm font-medium"
                  data-testid="legal-insurance-link"
                >
                  Read full Insurance Information (Legal)
                  <ExternalLink className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Do Not List Warning */}
        <Card className="border-red-200 bg-red-50 mb-8" data-testid="do-not-list-warning">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <Info className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-red-800 mb-2">Do not list unless you have confirmed suitable coverage</h2>
                <p className="text-red-700 text-sm">
                  Before listing your vehicle on HireHub, you must confirm with your insurer that 
                  peer-to-peer rental is permitted under your policy. Listing without appropriate 
                  coverage may leave you uninsured in the event of an incident.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Checklist Section */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            Insurance Checklist for Hosts
          </h2>
          
          <div className="space-y-4">
            {checklistItems.map((item, index) => (
              <Card key={index} className="border-slate-200" data-testid={`checklist-item-${index}`}>
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#004B8D]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#004B8D] font-semibold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Additional Tips */}
        <Card className="border-slate-200 bg-slate-50 mb-8">
          <CardContent className="p-6">
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Camera className="w-5 h-5 text-slate-600" />
              Evidence & Documentation Tips
            </h2>
            <ul className="space-y-3 text-slate-600 text-sm">
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Take dated photos of your vehicle before and after every rental</span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Photograph any existing damage clearly, from multiple angles</span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Record odometer readings at handover and return</span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Keep copies of all rental agreements and communications</span>
              </li>
              <li className="flex gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Report any incidents to your insurer promptly</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Contact Your Insurer */}
        <Card className="border-[#004B8D]/20 bg-[#004B8D]/5 mb-8">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <Phone className="w-6 h-6 text-[#004B8D] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-[#004B8D] mb-2">Contact Your Insurer</h2>
                <p className="text-slate-600 text-sm mb-3">
                  The most important step is to speak directly with your insurance provider. 
                  They can confirm what is and isn't covered under your specific policy.
                </p>
                <p className="text-slate-500 text-xs">
                  Have your policy number ready and ask specifically about "peer-to-peer vehicle rental" or "car sharing platforms".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="text-center space-y-4">
          <Link to="/legal/insurance" data-testid="full-legal-btn">
            <Button className="bg-[#004B8D] hover:bg-[#003a6d]">
              <FileText className="w-4 h-4 mr-2" />
              Read Full Insurance Information (Legal)
            </Button>
          </Link>
          
          <p className="text-slate-500 text-sm">
            For the complete legal disclosure, including disclaimers and liability information.
          </p>
          
          <div className="pt-4">
            <Link to="/register">
              <Button variant="outline" className="border-[#004B8D] text-[#004B8D] hover:bg-[#004B8D]/5">
                Ready to List Your Vehicle?
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} HireHub Australia. All rights reserved.</p>
          <p className="mt-2 text-xs">
            This guidance is for informational purposes only and does not constitute legal or insurance advice.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HostInsuranceGuidance;
